import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Bell, Search, ChevronDown, LogOut, Settings, User,
  ShieldCheck, AlertTriangle, RefreshCw, FileText, CreditCard, Send, X, Menu
} from "lucide-react";
import api from "../utils/api";
import { clearAuth } from "../store/authSlice";

const ROLE_LABELS = {
  super_admin: "Super Admin", local_admin: "Local Admin", owner: "Business Owner",
  consultant: "CFO / Consultant", ops_manager: "Ops Manager", accountant: "Accountant"
};

const EVENT_ICONS = {
  alert: AlertTriangle, sync: RefreshCw, report: FileText,
  billing: CreditCard, test: Send,
};
const EVENT_COLORS = {
  alert: "#EA580C", sync: "#1B75BA", report: "#9333EA",
  billing: "#D4A017", test: "#6B7491",
};
const EVENT_BG = {
  alert: "#FFF7ED", sync: "#EFF6FF", report: "#FDF4FF",
  billing: "#F0FDF4", test: "#F7F8FA",
};

export default function TopBar({ onOpenSidebar, viewport = "desktop" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, token } = useSelector((s) => s.auth);

  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const notifRef = useRef(null);
  const userRef = useRef(null);

  const now = new Date();
  const firstName = user?.name?.split(" ")[0] || "User";
  const isAdmin = user?.role === "super_admin" || user?.role === "local_admin";

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications?limit=5");
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifDropdown(false);
      if (userRef.current && !userRef.current.contains(e.target)) setShowUserDropdown(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.notification_id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const handleLogout = () => {
    localStorage.removeItem("onpoint_token");
    localStorage.removeItem("onpoint_refresh");
    dispatch(clearAuth());
    navigate("/login");
  };

  return (
    <header className="h-16 flex items-center px-3 sm:px-6 gap-2 sm:gap-4 sticky top-0 z-40 bg-white" style={{ borderBottom: "1px solid #CDD2DE" }}>
      {/* Mobile hamburger */}
      {viewport === "mobile" && (
        <button
          data-testid="topbar-mobile-menu"
          onClick={onOpenSidebar}
          className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg"
          style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE" }}
          aria-label="Open menu"
        >
          <Menu className="w-4 h-4" style={{ color: "#1C2040" }} />
        </button>
      )}

      {/* Search */}
      <div className="flex-1 flex items-center gap-2 rounded-lg px-3.5 h-10 max-w-sm min-w-0"
        style={{ backgroundColor: "#F7F8FA", border: "1.5px solid #CDD2DE" }}>
        <Search className="w-4 h-4 flex-shrink-0" style={{ color: "#6B7491" }} />
        <input data-testid="topbar-search" placeholder="Search Anything...."
          className="bg-transparent text-sm outline-none w-full min-w-0" style={{ color: "#1C2040" }}
          onFocus={(e) => e.target.parentElement.style.borderColor = "#1B75BA"}
          onBlur={(e) => e.target.parentElement.style.borderColor = "#CDD2DE"}
        />
      </div>

      <div className="flex-1" />

      {/* Notification Bell with Dropdown */}
      <div ref={notifRef} className="relative">
        <button data-testid="notifications-btn"
          className="relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors"
          style={{ backgroundColor: showNotifDropdown ? "#EFF6FF" : "#F7F8FA", border: `1.5px solid ${showNotifDropdown ? "#1B75BA" : "#CDD2DE"}` }}
          onClick={() => { setShowNotifDropdown(!showNotifDropdown); setShowUserDropdown(false); }}>
          <Bell className="w-4 h-4" style={{ color: showNotifDropdown ? "#1B75BA" : "#6B7491" }} />
          {unreadCount > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1"
              style={{ backgroundColor: "#EA580C" }}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {showNotifDropdown && (
          <div className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in"
            style={{ border: "1.5px solid #CDD2DE" }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #F7F8FA" }}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold" style={{ color: "#1C2040" }}>Notifications</span>
                {unreadCount > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: "#1B75BA" }}>
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button data-testid="notif-dropdown-mark-all" onClick={markAllRead}
                    className="text-[10px] font-semibold px-2 py-1 rounded hover:bg-gray-50" style={{ color: "#1B75BA" }}>
                    Mark all read
                  </button>
                )}
                <button onClick={() => setShowNotifDropdown(false)} className="p-1 rounded hover:bg-gray-100">
                  <X className="w-3.5 h-3.5" style={{ color: "#6B7491" }} />
                </button>
              </div>
            </div>

            {/* Notification Items */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: "#CDD2DE" }} />
                  <p className="text-xs" style={{ color: "#6B7491" }}>No notifications</p>
                </div>
              ) : (
                notifications.map((n) => {
                  const Icon = EVENT_ICONS[n.event_type] || AlertTriangle;
                  const color = EVENT_COLORS[n.event_type] || "#6B7491";
                  const bg = EVENT_BG[n.event_type] || "#F7F8FA";
                  return (
                    <div key={n.notification_id} data-testid={`notif-preview-${n.notification_id}`}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/50 cursor-pointer transition-colors"
                      style={{ backgroundColor: n.read ? "transparent" : "#FBFCFF", borderBottom: "1px solid #F7F8FA" }}
                      onClick={() => markRead(n.notification_id)}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-semibold truncate" style={{ color: "#1C2040" }}>{n.title}</p>
                          {!n.read && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#1B75BA" }} />}
                        </div>
                        <p className="text-[11px] truncate mt-0.5" style={{ color: "#6B7491" }}>{n.message}</p>
                        <span className="text-[10px]" style={{ color: "#9BA4B8" }}>
                          {new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <button data-testid="view-all-notifications-btn"
              className="w-full py-2.5 text-xs font-semibold text-center hover:bg-gray-50 transition-colors"
              style={{ color: "#1B75BA", borderTop: "1px solid #F7F8FA" }}
              onClick={() => { setShowNotifDropdown(false); navigate("/notifications"); }}>
              View All Notifications
            </button>
          </div>
        )}
      </div>

      {/* User Avatar with Dropdown */}
      <div ref={userRef} className="relative">
        <button data-testid="topbar-user-avatar"
          className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors"
          onClick={() => { setShowUserDropdown(!showUserDropdown); setShowNotifDropdown(false); }}>
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: "#282560" }}>
            {user?.name ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "?"}
          </div>
          <div className="hidden md:block text-left">
            <p className="text-xs font-semibold leading-none mb-0.5" style={{ color: "#1C2040" }}>{user?.name}</p>
            <p className="text-[10px]" style={{ color: "#6B7491" }}>{ROLE_LABELS[user?.role] || user?.role}</p>
          </div>
          <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ color: "#6B7491", transform: showUserDropdown ? "rotate(180deg)" : "none" }} />
        </button>

        {showUserDropdown && (
          <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in"
            style={{ border: "1.5px solid #CDD2DE" }}>
            {/* User Info */}
            <div className="px-4 py-3" style={{ borderBottom: "1px solid #F7F8FA", backgroundColor: "#FBFCFF" }}>
              <p className="text-xs font-bold" style={{ color: "#1C2040" }}>{user?.name}</p>
              <p className="text-[10px] mt-0.5" style={{ color: "#6B7491" }}>{user?.email}</p>
              <span className="inline-block mt-1.5 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase"
                style={{ backgroundColor: "#EFF6FF", color: "#1B75BA" }}>
                {ROLE_LABELS[user?.role] || user?.role}
              </span>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <button data-testid="user-menu-settings" onClick={() => { setShowUserDropdown(false); navigate("/settings"); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-gray-50 transition-colors text-left"
                style={{ color: "#1C2040" }}>
                <Settings className="w-3.5 h-3.5" style={{ color: "#6B7491" }} /> Profile & Settings
              </button>

              <button data-testid="user-menu-notifications" onClick={() => { setShowUserDropdown(false); navigate("/notifications"); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-gray-50 transition-colors text-left"
                style={{ color: "#1C2040" }}>
                <Bell className="w-3.5 h-3.5" style={{ color: "#6B7491" }} /> Notifications
                {unreadCount > 0 && (
                  <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: "#EA580C" }}>
                    {unreadCount}
                  </span>
                )}
              </button>

              {isAdmin && (
                <button data-testid="user-menu-admin" onClick={() => { setShowUserDropdown(false); navigate("/admin/dashboard"); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-gray-50 transition-colors text-left"
                  style={{ color: "#1C2040" }}>
                  <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#6B7491" }} /> Admin Panel
                </button>
              )}

              <button data-testid="user-menu-help" onClick={() => { setShowUserDropdown(false); navigate("/help"); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-gray-50 transition-colors text-left"
                style={{ color: "#1C2040" }}>
                <User className="w-3.5 h-3.5" style={{ color: "#6B7491" }} /> Help & Support
              </button>
            </div>

            {/* Logout */}
            <div style={{ borderTop: "1px solid #F7F8FA" }}>
              <button data-testid="user-menu-logout" onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs hover:bg-orange-50 transition-colors text-left"
                style={{ color: "#EA580C" }}>
                <LogOut className="w-3.5 h-3.5" /> Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
